import { useState } from 'react'
import { TableCell, TableRow, Chip } from '@mui/material'
import ActionMenu from 'components/actionMenu'
import Content from 'components/content/content'
import {
  useGetInboxListQuery,
  useUpdateNotificationMutation,
  useUpdateAllNotificationsMutation,
  useGetInboxCountQuery
} from 'store/inbox'
import GLOBAL from 'modules/global'
import type { NotificationProps } from 'modules/types'
import InboxDetail from './detail'

type ColumnsType = Array<{
  label: string
  key: keyof NotificationProps
  render?: (d: NotificationProps) => React.ReactNode
}>

const InboxList = () => {
  const [payload, setPayload] = useState({
    start: 0,
    length: 10
  })
  const [selectedId, setSelectedId] = useState<string | number | null>(null)
  const { data, isLoading } = useGetInboxListQuery(payload)
  const [markAsRead] = useUpdateNotificationMutation()
  const [markAllRead, { isLoading: isMarkingAllRead }] = useUpdateAllNotificationsMutation()
  const { data: notificationCount } = useGetInboxCountQuery()

  function onChangePage(value: number) {
    setPayload((prev) => ({ ...prev, start: value - 1 }))
  }

  function onChangePerPage(value: number) {
    setPayload((prev) => ({ ...prev, length: value, start: 0 }))
  }

  function handleClose() {
    setSelectedId(null)
  }

  function handleMarkAllAsRead() {
    if (notificationCount && notificationCount.noRead > 0) {
      markAllRead()
    }
  }

  async function handleViewClick(item: NotificationProps) {
    // Mark notification as read if it's not already read
    if (!item.isRead && item.id) {
      try {
        await markAsRead({
          id: item.id,
          isRead: true
        }).unwrap()
      } catch (error) {
        console.error('Failed to mark notification as read:', error)
      }
    }
    setSelectedId(item.id as number)
  }

  const columns: ColumnsType = [
    { label: 'ID', key: 'id' },
    { label: 'Type', key: 'notifTypeName' },
    { label: 'Title', key: 'title' },
    { label: 'Body', key: 'body' },
    {
      label: 'Read Status',
      key: 'isRead',
      render: (d: NotificationProps) => (
        <Chip
          label={d.isRead ? 'Read' : 'Unread'}
          color={d.isRead ? 'default' : 'primary'}
          size='small'
        />
      )
    },
    { label: 'Created At', key: 'createdAtFormated' }
  ]

  return (
    <>
      <Content
        title='Inbox List'
        tableHeadTitles={columns.map((k) => k.label)}
        isLoading={isLoading}
        pagination={onChangePage}
        totalPage={data && GLOBAL.tableTotalPage(data?.recordsTotal)}
        totalRecords={data?.recordsTotal || 0}
        page={payload.start + 1}
        onChangePerPage={onChangePerPage}
        onAdditional={handleMarkAllAsRead}
        additionalTitle='Mark All as Read'
        additionalLoading={isMarkingAllRead}
      >
        {(data?.data || []).map((item, index) => (
          <TableRow key={item.id}>
            <TableCell>{(payload.start * payload.length) + index + 1}</TableCell>
            <TableCell>
              <ActionMenu
                id={`inbox-${item.id}`}
                menus={[
                  {
                    label: 'View',
                    onClick: () => handleViewClick(item)
                  }
                ]}
              />
            </TableCell>
            {columns.map((col) => (
              <TableCell key={col.key}>
                {col.render ? col.render(item) : item[col.key as keyof NotificationProps]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </Content>
      <InboxDetail id={selectedId as number} open={!!selectedId} onClose={handleClose} />
    </>
  )
}

export default InboxList
